import { Bar, BarChart, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";

import { money } from "./format";
import { ORDERS_COPY, type OrderRow } from "./orders";

/** The only place a money string becomes a number: bar heights are layout, never a figure shown as fact (UX subset v0.3). */
const n = (value: string | null): number => (value == null ? 0 : Number(value));

const FILL = {
  kept: "var(--color-ks-pass)", refund: "url(#pp-refund-hatch)",
  cogs: "var(--color-ks-purple)", pick_pack: "var(--color-ks-purple-2)", packaging: "var(--color-ks-purple-3)", courier: "var(--color-ks-purple-shade)",
};

/** The revenue bar: revenue ex VAT, which already holds the customer-paid shipping (C4 v1.12), with the refund hatched out of the top. */
export function revenueBar(order: OrderRow): { kept: number; refund: number } {
  const refund = n(order.refund_amount);
  return { kept: n(order.revenue_ex_vat) - refund, refund };
}

/**
 * Two bars on one scale: revenue (ex VAT, shipping already inside it, the refund hatched out of the top) beside the
 * costs stacked (COGS, pick and pack, packaging, courier). The legend beside it carries the API's own strings; the
 * chart only sizes the bars. A cost the order does not carry is absent from the stack and named under the chart.
 */
export function OrderChart({ order }: { order: OrderRow }) {
  const cur = order.currency, fc = order.fee_components;
  const { kept, refund } = revenueBar(order);
  const data = [
    { name: "Revenue", kept, refund },
    { name: "Costs", cogs: n(order.cogs), pick_pack: n(fc.pick_pack), packaging: n(fc.packaging), courier: n(fc.courier) },
  ];
  const missing: string[] = [...(order.cogs == null ? [ORDERS_COPY.sheet.noCogsRecord] : []), ...(fc.pick_pack == null ? [ORDERS_COPY.sheet.noBilled] : [])];
  const legend: Array<[string, string, string | null]> = [
    [FILL.kept, ORDERS_COPY.sheet.revenue, money(order.revenue_ex_vat, cur)],
    ["", ORDERS_COPY.sheet.shippingWithin, order.customer_paid_shipping === "0.00" ? ORDERS_COPY.free : money(order.customer_paid_shipping, cur)],
    ...(order.refund_state !== "none" ? [[FILL.refund, `${ORDERS_COPY.sheet.refund} (${order.refund_state})`, `−${money(order.refund_amount, cur)}`] as [string, string, string]] : []),
    ...(order.cogs != null ? [[FILL.cogs, ORDERS_COPY.sheet.cogs, `−${money(order.cogs, cur)}`] as [string, string, string]] : []),
    ...(fc.pick_pack != null ? [[FILL.pick_pack, ORDERS_COPY.sheet.pickPack, `−${money(fc.pick_pack, cur)}`] as [string, string, string]] : []),
    ...(fc.packaging != null ? [[FILL.packaging, ORDERS_COPY.sheet.packaging, `−${money(fc.packaging, cur)}`] as [string, string, string]] : []),
    ...(fc.courier != null ? [[FILL.courier, ORDERS_COPY.sheet.courier, `−${money(fc.courier, cur)}`] as [string, string, string]] : []),
  ];
  return (
    <div className="flex flex-wrap items-start gap-4">
      <div className="w-64">
        <ChartFrame title={ORDERS_COPY.sheet.chart} height={200}>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barCategoryGap="30%">
            <defs>
              <pattern id="pp-refund-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width="6" height="6" fill="var(--color-ks-pass)" />
                <line x1="0" y1="0" x2="0" y2="6" stroke="var(--color-ks-fail)" strokeWidth="2" />
              </pattern>
            </defs>
            <XAxis dataKey="name" tickLine={false} axisLine={{ stroke: "var(--color-ks-grey-3)" }} tick={{ fontSize: 12, fill: "var(--color-ks-ink)" }} />
            <YAxis hide />
            <Bar dataKey="kept" stackId="a" fill={FILL.kept} isAnimationActive={false} />
            <Bar dataKey="refund" stackId="a" fill={FILL.refund} isAnimationActive={false} />
            <Bar dataKey="cogs" stackId="a" fill={FILL.cogs} isAnimationActive={false} />
            <Bar dataKey="pick_pack" stackId="a" fill={FILL.pick_pack} isAnimationActive={false} />
            <Bar dataKey="packaging" stackId="a" fill={FILL.packaging} isAnimationActive={false} />
            <Bar dataKey="courier" stackId="a" fill={FILL.courier} isAnimationActive={false} />
          </BarChart>
        </ChartFrame>
      </div>
      <dl className="min-w-52 flex-1 text-sm">
        {legend.map(([fill, label, value], i) => (
          <div key={i} className="flex items-center gap-2 py-0.5">
            <span aria-hidden className="inline-block size-3 rounded-sm" style={{ background: fill === "" ? "transparent" : fill.startsWith("url(") ? "repeating-linear-gradient(45deg, var(--color-ks-fail) 0 2px, var(--color-ks-pass) 2px 5px)" : fill }} />
            <dt className="text-ks-grey-1">{label}</dt>
            <dd className="ml-auto font-semibold tabular-nums text-ks-ink">{value}</dd>
          </div>
        ))}
        <div className="mt-1 flex items-center gap-2 border-t border-ks-grey-3 pt-1">
          <dt className="font-semibold text-ks-ink">{order.profit == null ? ORDERS_COPY.sheet.profit : order.loss_flag ? ORDERS_COPY.loss : ORDERS_COPY.sheet.profit}</dt>
          <dd className={`ml-auto font-semibold tabular-nums ${order.profit == null ? "text-ks-grey-1" : order.loss_flag ? "text-ks-warn" : "text-ks-pass"}`}>{order.profit == null ? ORDERS_COPY.na : signedMoney(order.profit, cur)}</dd>
        </div>
        {missing.length > 0 && <p className="mt-1 text-xs text-ks-grey-1">{ORDERS_COPY.sheet.notInStack(missing.join(", "))}</p>}
      </dl>
    </div>
  );
}

/** "+£14.39" or "−£34.45": the sign is read off the string, the money is formatted from the string. */
export function signedMoney(value: string, currency: string): string {
  return value.startsWith("-") ? `−${money(value.slice(1), currency)}` : `+${money(value, currency)}`;
}
